import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n-composable';
import { usePattern } from '../../../common/js/composables/pattern';
import { FULLNAME_REGEXP } from '../../../common/js/constants/constants';

export const personalData = {
	name: null,
	birthName: null,
	birthPlace: null,
	birthDate: null,
	mothersName: null,
	zipCode: null,
	settlement: null,
	publicArea: null,
	publicAreaType: null,
	houseNumber: null,
	identityType: null,
	identityNumber: null
};
const companyData = {
	companyName: null,
	companyShortName: null,
	companyZipCode: null,
	companySettlement: null,
	companyPublicArea: null,
	companyPublicAreaType: null,
	companyHouseNumber: null,
	companyRegistrationNumber: null,
	companyTaxNumber: null
};
const model = reactive({
	powerOfAttorney: { ...personalData },
	representative: null,
	...companyData,
	authorized: { ...personalData },
	captchaKey: null,
	captchaText: null
});
const hasRepresentative = ref(false);
const switchRepresentative = () => {
	hasRepresentative.value = !hasRepresentative.value;
	model.representative = hasRepresentative.value ? { ...personalData } : null;
};
const clearAuthorized = () => {
	Object.assign(model, structuredClone({ ...model, authorized: { ...personalData } }));
};
export const useForm = (captchaImg = ref(null), captchaError = ref(null)) => {
	const { t } = useI18n();
	const { pattern } = usePattern();
	const personalDataRules = computed(() => ({
		name: {
			required: helpers.withMessage(t('name.required'), required),
			pattern: helpers.withMessage(t('name.pattern'), pattern(FULLNAME_REGEXP))
		},
		birthName: {
			required: helpers.withMessage(t('birthName.required'), required),
			pattern: helpers.withMessage(t('birthName.pattern'), pattern(FULLNAME_REGEXP))
		},
		birthPlace: {
			required: helpers.withMessage(t('birthPlace.required'), required)
		},
		birthDate: {
			required: helpers.withMessage(t('birthDate.required'), required)
		},
		mothersName: {
			required: helpers.withMessage(t('mothersName.required'), required),
			pattern: helpers.withMessage(t('mothersName.pattern'), pattern(FULLNAME_REGEXP))
		},
		zipCode: {
			required: helpers.withMessage(t('zipCode.required'), required)
		},
		settlement: {
			required: helpers.withMessage(t('settlement.required'), required)
		},
		publicArea: {
			required: helpers.withMessage(t('publicArea.required'), required)
		},
		publicAreaType: {
			required: helpers.withMessage(t('publicAreaType.required'), required)
		},
		houseNumber: {
			required: helpers.withMessage(t('houseNumber.required'), required)
		},
		identityType: {
			required: helpers.withMessage(t('identityType.required'), required)
		},
		identityNumber: {
			required: helpers.withMessage(t('identityNumber.required'), required)
		}
	}));
	const rules = computed(() => ({
		powerOfAttorney: personalDataRules.value,
		representative: hasRepresentative.value ? personalDataRules.value : {},
		companyName: {
			required: helpers.withMessage(t('companyName.required'), required)
		},
		companyShortName: {
			required: helpers.withMessage(t('companyShortName.required'), required)
		},
		companyZipCode: {
			required: helpers.withMessage(t('companyZipCode.required'), required)
		},
		companySettlement: {
			required: helpers.withMessage(t('companySettlement.required'), required)
		},
		companyPublicArea: {
			required: helpers.withMessage(t('companyPublicArea.required'), required)
		},
		companyPublicAreaType: {
			required: helpers.withMessage(t('companyPublicAreaType.required'), required)
		},
		companyHouseNumber: {
			required: helpers.withMessage(t('companyHouseNumber.required'), required)
		},
		companyRegistrationNumber: {
			required: helpers.withMessage(t('companyRegistrationNumber.required'), required)
		},
		companyTaxNumber: {
			required: helpers.withMessage(t('companyTaxNumber.required'), required)
		},
		authorized: personalDataRules.value,
		captchaText: {
			required: captchaImg.value ? helpers.withMessage(t('captchaText.required'), required) : {},
			invalid: captchaImg.value ? helpers.withMessage(t('captchaText.invalid'), () => captchaError.value === false) : {}
		}
	}));
	const v$ = useVuelidate(rules, model);
	return { model, rules, hasRepresentative, switchRepresentative, clearAuthorized, v$ };
};
